import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Card, Skeleton, Space, Tag, Typography } from "antd"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import InPersonMeetCreatedPreview from "./InPersonMeetCreatedPreview"
import SeasonMeetCard, { ISeasonMeetWithClubs } from "./SeasonMeetCard"
import SharedAcceptedVirtualMeetCard, {
  ISharedAcceptedVirtualMeet,
  ISharedVirtualMeetParticipantOverview,
} from "./SharedAcceptedVirtualMeetCard"
import { gen, swimminglyApi } from "./utils"

const { Text } = Typography

const ACTIVITY_FEED_REFETCH_EVENT = "swimmingly:activity-feed:refetch"

type GuardianMeetLifecycleItem = {
  clubId?: number | null
  meetId?: number | null
  context?: Record<string, any>
}

function titleize(value: string | null | undefined) {
  if (!value) return "Unknown"

  return String(value)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase())
}

function GuardianSwimmersPreview({
  item,
}: {
  item: GuardianMeetLifecycleItem
}): JSX.Element {
  const recipientSnapshot = item.context?.recipientSnapshot || {}
  const swimmers = Array.isArray(item.context?.swimmers) ? item.context.swimmers : []
  const hasLiveSwimmerData = Array.isArray(item.context?.swimmers)

  if (!recipientSnapshot.hasGuardianContext) {
    return (
      <Alert
        showIcon
        type="info"
        title="Swimmer details are only available for guardian recipients."
      />
    )
  }

  if (!hasLiveSwimmerData) {
    return <Skeleton active paragraph={{ rows: 4 }} />
  }

  return (
    <Card size="small" title="Your Swimmers">
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        {swimmers.length === 0 ? (
          <Text type="secondary">
            No swimmer declarations are currently tied to your account for this meet.
          </Text>
        ) : (
          swimmers.map((swimmer: any) => (
            <Card key={swimmer.swimmerId} size="small" styles={{ body: { padding: 12 } }}>
              <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                <Space wrap>
                  <Text strong>
                    {[swimmer.firstName, swimmer.lastName].filter(Boolean).join(" ")}
                  </Text>
                  <Tag
                    color={
                      swimmer.status === "attending"
                        ? "green"
                        : swimmer.status === "notAttending"
                          ? "red"
                          : "gold"
                    }
                  >
                    {titleize(swimmer.status)}
                  </Tag>
                </Space>
                {swimmer.lastDeclarationActorName ? (
                  <Text type="secondary">
                    Last updated by {swimmer.lastDeclarationActorName}
                  </Text>
                ) : null}
              </Space>
            </Card>
          ))
        )}
      </Space>
    </Card>
  )
}

function GuardianAcceptedVirtualMeetPreview({
  item,
}: {
  item: GuardianMeetLifecycleItem
}): JSX.Element | null {
  const clubId = Number(item.clubId || item.context?.clubId || 0)
  const virtualMeetInvitationId = Number(item.context?.virtualMeetInvitationId || 0)
  const virtualMeetId = Number(item.context?.virtualMeetId || 0)
  const [loading, setLoading] = useState(false)
  const [virtualMeet, setVirtualMeet] = useState<ISharedAcceptedVirtualMeet | null>(null)
  const [participants, setParticipants] = useState<ISharedVirtualMeetParticipantOverview[]>([])
  const [error, setError] = useState<string | null>(null)
  const [acceptedLegsExpanded, setAcceptedLegsExpanded] = useState(true)

  const missingIds = !clubId || (!virtualMeetInvitationId && !virtualMeetId)

  const loadPreview = useCallback(async () => {
    if (missingIds) {
      setVirtualMeet(null)
      setParticipants([])
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const resolvedVirtualMeetId = Number(virtualMeetId || 0)

      if (!resolvedVirtualMeetId) {
        throw new Error("Unable to load this virtual meet preview.")
      }

      const participantsResponse: any = await swimminglyApi.get(
        gen(`/api/getVirtualMeetParticipantsOverview/${resolvedVirtualMeetId}`),
      )
      const nextParticipants: ISharedVirtualMeetParticipantOverview[] = Array.isArray(
        participantsResponse?.participants,
      )
        ? participantsResponse.participants
        : []

      const fallbackVirtualMeet: ISharedAcceptedVirtualMeet = {
        virtualMeetInvitationId,
        virtualMeetId: resolvedVirtualMeetId,
        clubId,
        acceptedMeetId: Number(item.context?.meetId || item.meetId || 0) || null,
        meetName:
          String(item.context?.virtualMeetName || item.context?.meetName || "Virtual meet") ||
          "Virtual meet",
        meetdate:
          String(item.context?.virtualMeetDate || item.context?.meetDate || item.context?.meetdate || "") ||
          "",
        startdate:
          item.context?.virtualMeetStartDate || item.context?.startdate || null,
        course: Number(item.context?.virtualMeetCourse || item.context?.course || 0),
        organizerClubId:
          Number(item.context?.organizerClubId || participantsResponse?.organizerClubId || 0),
        organizerClubName: item.context?.organizerClubName || null,
        organizerClubNameLong: item.context?.organizerClubNameLong || null,
        pendingInvitations: nextParticipants.filter((participant) => participant.status === "pending")
          .length,
        acceptedInvitations: nextParticipants.filter((participant) => participant.status === "accepted")
          .length,
        declinedInvitations: nextParticipants.filter((participant) => participant.status === "declined")
          .length,
        removedInvitations: nextParticipants.filter((participant) => participant.status === "removed")
          .length,
      }

      setVirtualMeet(fallbackVirtualMeet)
      setParticipants(nextParticipants)
    } catch (err: any) {
      setVirtualMeet(null)
      setParticipants([])
      setError(err?.message || "Unable to load this virtual meet preview.")
    } finally {
      setLoading(false)
    }
  }, [clubId, item.context, item.meetId, missingIds, virtualMeetId, virtualMeetInvitationId])

  useEffect(() => {
    void loadPreview()
  }, [loadPreview])

  useEffect(() => {
    if (missingIds) {
      return
    }

    const handleRefresh = () => {
      void loadPreview()
    }

    window.addEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)

    return () => {
      window.removeEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)
    }
  }, [loadPreview, missingIds])

  const activeClubId = useMemo(() => (clubId > 0 ? clubId : null), [clubId])

  if (missingIds) {
    return (
      <Alert
        showIcon
        type="info"
        title="Open this virtual swim meet from the action button below."
      />
    )
  }

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }

  if (error) {
    return <Alert showIcon type="info" title={error} />
  }

  if (!virtualMeet) {
    return null
  }

  return (
    <SharedAcceptedVirtualMeetCard
      virtualMeet={virtualMeet}
      participants={participants}
      loadingParticipants={loading}
      activeClubId={activeClubId}
      acceptedLegsExpanded={acceptedLegsExpanded}
      onToggleAcceptedLegs={() => setAcceptedLegsExpanded((current) => !current)}
    />
  )
}

function GuardianHeatSheetMergedPreview({
  item,
}: {
  item: GuardianMeetLifecycleItem
}): JSX.Element | null {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const clubId = Number(item.clubId || item.context?.clubId || 0)
  const meetId = Number(item.meetId || item.context?.meetId || 0)
  const seasonId = Number(item.context?.seasonId || 0)
  const [loading, setLoading] = useState(false)
  const [meet, setMeet] = useState<ISeasonMeetWithClubs | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [teamsExpanded, setTeamsExpanded] = useState(false)

  const missingIds = !clubId || !meetId || !user?.userId

  const loadPreview = useCallback(async () => {
    if (missingIds) {
      setMeet(null)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response: any = await swimminglyApi
        .post(gen("/api/getMeetScheduleForParents"))
        .body({
          userId: user?.userId,
          clubId,
          seasonId: seasonId || null,
        })

      const eventsGroupedByYear = response?.eventsGroupedByYear || {}
      const clubsByClubId = response?.clubsByClubId || {}
      const allMeets = Object.values(eventsGroupedByYear).flat() as any[]
      const matchedMeet = allMeets.find((candidate) => Number(candidate?.meetId) === meetId)

      if (!matchedMeet) {
        throw new Error("Unable to load this swim meet preview.")
      }

      const meetClubs = Array.isArray(matchedMeet.clubIdsForClubsInMeet)
        ? matchedMeet.clubIdsForClubsInMeet
            .map((candidateClubId: string | number) => clubsByClubId[String(candidateClubId)])
            .filter(Boolean)
        : []

      const normalizedMeet: ISeasonMeetWithClubs = {
        meet: {
          ...matchedMeet,
          meetId,
          meet_date:
            matchedMeet.meet_date || matchedMeet.endDate || matchedMeet.startDate,
          meetstatus:
            matchedMeet.meetstatus === null || matchedMeet.meetstatus === undefined
              ? 1
              : Number(matchedMeet.meetstatus),
        },
        meetCourse:
          matchedMeet.unit_of_measure === 1
            ? "SCM"
            : matchedMeet.unit_of_measure === 2
              ? "LCM"
              : "SCY",
        virtualMeetCourse:
          matchedMeet.virtualCourse === 1
            ? "SCM"
            : matchedMeet.virtualCourse === 2
              ? "LCM"
              : matchedMeet.isVirtualMeet
                ? "SCY"
                : null,
        isVirtualMeet: Boolean(matchedMeet.isVirtualMeet),
        startdate: matchedMeet.startDate || undefined,
        clubs: meetClubs.map((club: any) => ({
          club,
          meetstatus:
            matchedMeet.meetstatus === null || matchedMeet.meetstatus === undefined
              ? 1
              : Number(matchedMeet.meetstatus),
          entriesCount: 0,
          confirmLineup: false,
          heatSheetAccepted: Number(matchedMeet.meetstatus || 0) >= 1,
        })),
      }

      setMeet(normalizedMeet)
      setTeamsExpanded(normalizedMeet.clubs.length <= 4)
    } catch (err: any) {
      setMeet(null)
      setError(err?.message || "Unable to load this swim meet preview.")
    } finally {
      setLoading(false)
    }
  }, [clubId, meetId, missingIds, seasonId, user?.userId])

  useEffect(() => {
    void loadPreview()
  }, [loadPreview])

  useEffect(() => {
    if (missingIds) {
      return
    }

    const handleRefresh = () => {
      void loadPreview()
    }

    window.addEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)

    return () => {
      window.removeEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)
    }
  }, [loadPreview, missingIds])

  if (missingIds) {
    return (
      <Alert
        showIcon
        type="success"
        title="Your heat sheet has been merged for this meet"
      />
    )
  }

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }

  if (error) {
    return (
      <Alert
        showIcon
        type="success"
        title="Your heat sheet has been merged for this meet"
      />
    )
  }

  if (!meet) {
    return null
  }

  return (
    <SeasonMeetCard
      meet={meet}
      viewMode="regular"
      teamsExpanded={teamsExpanded}
      onToggleTeams={() => setTeamsExpanded((current) => !current)}
    />
  )
}

export default function GuardianMeetLifecyclePreview({
  item,
}: {
  item: GuardianMeetLifecycleItem
}): JSX.Element | null {
  const lifecycleState = String(item.context?.lifecycleState || "")
  const hasVirtualCreatedContext =
    lifecycleState === "created" &&
    (Number(item.context?.virtualMeetInvitationId || 0) > 0 ||
      Number(item.context?.virtualMeetId || 0) > 0)

  if (lifecycleState === "declarations_open" || lifecycleState === "declarations_closed") {
    return <GuardianSwimmersPreview item={item} />
  }

  if (hasVirtualCreatedContext) {
    return <GuardianAcceptedVirtualMeetPreview item={item} />
  }

  if (lifecycleState === "heat_sheet_merged") {
    // can't add heat sheet meet card treatment until we add an admin AFN for heat sheet merged, so that we can repurpose that component
    return <GuardianHeatSheetMergedPreview item={item} />
  }

  if (
    lifecycleState === "created" ||
    lifecycleState === "results_published"
  ) {
    return <InPersonMeetCreatedPreview item={item} />
  }

  return null
}